import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useModulePermissions, type ModuleKey } from "@/hooks/useModulePermissions";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMarketScope } from "@/hooks/useMarketScope";
import { useAdminMarket } from "@/hooks/useAdminMarket";
import { filterModulesByMarket } from "@/lib/adminMarketModules";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  LayoutDashboard, Wrench, Users, Warehouse, Calculator, FileStack, Globe, ShoppingBag, Star, Boxes, Sparkles,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ToconlineConnectionButton } from "@/components/shared/ToconlineConnectionButton";

const modules = [
  { path: "/admin", label: "Admin", icon: LayoutDashboard, activeColor: "bg-violet-500 text-white", moduleKey: "admin" as ModuleKey },
  { path: "/crm", label: "CRM", icon: Users, activeColor: "bg-emerald-500 text-white", moduleKey: "crm" as ModuleKey },
  { path: "/assistencia-tecnica", label: "Assistência", icon: Wrench, activeColor: "bg-amber-500 text-white", moduleKey: "assistencia-tecnica" as ModuleKey },
  { path: "/armazem", label: "Armazém", icon: Warehouse, activeColor: "bg-blue-500 text-white", moduleKey: "armazem" as ModuleKey },
  { path: "/catalogo", label: "Catálogo", icon: Boxes, activeColor: "bg-sky-500 text-white", moduleKey: "catalog" as ModuleKey },
  { path: "/contabilidade", label: "Contab.", icon: Calculator, activeColor: "bg-rose-500 text-white", moduleKey: "contabilidade" as ModuleKey },
  { path: "/ia", label: "IA", icon: Sparkles, activeColor: "bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white", moduleKey: "ia" as ModuleKey },
  { path: "/burocracia", label: "Buroc.", icon: FileStack, activeColor: "bg-indigo-500 text-white", moduleKey: "burocracia" as ModuleKey },
  { path: "/procurement", label: "Compras", icon: ShoppingBag, activeColor: "bg-orange-500 text-white", moduleKey: "procurement" as ModuleKey },
];

export function ModuleDock() {
  const location = useLocation();
  const { isAdmin, user, profile } = useAuth();
  const { hasModuleAccess, isLoading: permLoading } = useModulePermissions();
  const isMobile = useIsMobile();
  const { scope } = useMarketScope();
  const { market, switchMarket } = useAdminMarket();

  const preferredModule = profile?.preferred_module || null;

  if (!user) return null;

  // All modules stay available in both markets; data isolation is enforced
  // server-side via market_scope_isolation RLS policies.
  const marketModules = filterModulesByMarket(modules, market);

  // Admin sees all modules; others only see explicitly permitted ones
  const availableModules = isAdmin
    ? marketModules
    : marketModules.filter(module => hasModuleAccess(module.moduleKey));

  // While loading, only show for admins to avoid flash
  if (permLoading && !isAdmin) return null;

  // No modules available = no dock
  if (availableModules.length === 0) return null;

  const handleSetPreferred = async (moduleKey: ModuleKey) => {
    if (!user) return;
    const newValue = preferredModule === moduleKey ? null : moduleKey;
    const { error } = await supabase
      .from("profiles")
      .update({ preferred_module: newValue })
      .eq("id", user.id);
    if (error) {
      toast.error("Erro ao definir módulo favorito");
    } else {
      toast.success(newValue ? `Módulo favorito: ${modules.find(m => m.moduleKey === newValue)?.label}` : "Módulo favorito removido");
      window.location.reload();
    }
  };

  // Detect Lovable preview iframe — its chrome covers top:0 and steals clicks
  const inLovablePreview = typeof window !== "undefined"
    && window.self !== window.top
    && /lovable\.(app|dev)/.test(window.location.hostname);
  const topOffset = inLovablePreview ? "top-10" : "top-0";

  return (
    <>
      <div className={cn("w-full", inLovablePreview ? "h-[4.75rem]" : "h-9")} />
      <div className={cn("fixed left-0 right-0 z-[9999]", topOffset)}>
        <TooltipProvider delayDuration={100}>
          <nav className="flex items-center justify-center gap-1 px-3 py-1 bg-slate-900 border-b border-slate-700 shadow-md">
          {availableModules.map((module) => {
            const Icon = module.icon;
            const isActive = location.pathname.startsWith(module.path);
            const isFav = preferredModule === module.moduleKey;
            return (
              <div key={module.path} className="flex items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to={module.path}
                      className={cn(
                        "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold transition-all",
                        isActive ? module.activeColor + " shadow-sm" : "text-slate-400 hover:bg-slate-800 hover:text-white"
                      )}>
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      {!isMobile && <span>{module.label}</span>}
                      {isFav && <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="flex flex-col items-center gap-1">
                    <p>{module.label}</p>
                    {availableModules.length > 1 && (
                      <button onClick={(e) => { e.stopPropagation(); handleSetPreferred(module.moduleKey); }}
                        className="text-[10px] text-yellow-400 hover:text-yellow-300 flex items-center gap-1">
                        <Star className={cn("h-3 w-3", isFav ? "fill-yellow-400" : "")} />
                        {isFav ? "Remover favorito" : "Definir como favorito"}
                      </button>
                    )}
                  </TooltipContent>
                </Tooltip>
              </div>
            );
          })}
          {scope === "global" && (
            <>
              <div className="w-px h-4 bg-slate-700 mx-1" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => switchMarket(market === "pt" ? "br" : "pt")}
                    className={cn(
                      "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold transition-all",
                      market === "br"
                        ? "bg-yellow-500 text-slate-900 shadow-sm"
                        : "bg-slate-800 text-slate-300 hover:text-white"
                    )}>
                    <span className="text-sm leading-none">{market === "br" ? "🇧🇷" : "🇵🇹"}</span>
                    {!isMobile && <span>{market === "br" ? "Brasil" : "Portugal"}</span>}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Ver dados de {market === "br" ? "Portugal" : "Brasil"}</p>
                </TooltipContent>
              </Tooltip>
            </>
          )}
          {isAdmin && (
            <>
              <div className="w-px h-4 bg-slate-700 mx-1" />
              <ToconlineConnectionButton compact className="h-7 px-2 text-xs" />
            </>
          )}
          <div className="w-px h-4 bg-slate-700 mx-1" />
          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/"
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold transition-all",
                  location.pathname === "/" ? "bg-white text-slate-900 shadow-sm" : "text-slate-400 hover:bg-slate-800 hover:text-white"
                )}>
                <Globe className="h-3.5 w-3.5 shrink-0" />
                {!isMobile && <span>Site</span>}
              </Link>
            </TooltipTrigger>
            {isMobile && (
              <TooltipContent side="bottom"><p>Ir para o Site</p></TooltipContent>
            )}
          </Tooltip>
          </nav>
        </TooltipProvider>
      </div>
    </>
  );
}
